import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest, getMsalRedirectError } from "../config/authConfig";
import { msalErrorMessage } from "../utils/msalErrorMessage";

const isDeployPreview = /^deploy-preview-\d+--/.test(window.location.hostname);

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

// Mirrors authConfig's redirectUri. Named in the mismatch error so an admin can
// copy the exact string Entra expects — under MSAL v5 that is the redirect
// bridge path, not the bare origin.
const MSAL_REDIRECT_URI = `${window.location.origin}/redirect.html`;

export default function LoginPage() {
  const { instance } = useMsal();
  const [loggingIn, setLoggingIn] = useState(false);
  // Read once per render: the error is captured during MSAL init, before React
  // mounts, so it never changes while this page is up.
  const redirectError = getMsalRedirectError();

  const handleLogin = () => {
    if (loggingIn) return;
    setLoggingIn(true);
    // Save the current URL so we can return to it after Azure AD redirects back
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/') {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);
    }
    instance.loginRedirect(loginRequest).catch(() => {
      setLoggingIn(false);
    });
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="bg-white rounded-ae-sm shadow-card p-8 max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <img
            src="/activ-energy-logo.png"
            alt="Activ Energy"
            className="h-16"
          />
        </div>

        {redirectError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <p className="text-sm font-semibold text-red-800 mb-1">Sign-in error</p>
            {/*
              Never render the MSAL message directly. Under v5 it is a
              documentation URL ("See https://aka.ms/msal.js.errors#... for
              details"), so msalErrorMessage maps the error *code* to copy the
              reader can act on. See src/utils/msalErrorMessage.js.
            */}
            <p className="text-sm text-red-700">
              {msalErrorMessage(redirectError, MSAL_REDIRECT_URI)}
            </p>
          </div>
        )}

        {isDeployPreview && !redirectError && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
            <p className="text-sm font-semibold text-amber-800 mb-1">Deploy Preview</p>
            <p className="text-sm text-amber-700">
              Sign-in may fail if this preview URL has not been registered in Azure AD.
              Check the build log for warnings from the Azure Redirect Plugin.
            </p>
          </div>
        )}

        <div className="mb-8">
          <p className="text-gray-600 mb-2">
            Sign in with your Microsoft account to access the Activ OS.
          </p>
        </div>

        <button
          onClick={handleLogin}
          disabled={loggingIn}
          className="w-full bg-activ-teal hover:bg-activ-navy text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loggingIn ? (
            <>
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Signing in…
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </>
          )}
        </button>
      </div>
    </div>
  );
}
